import { CONSENT_ONE_TRUST_GROUP_MAPPING, ONE_TRUST_CONSENT_COUNTRIES } from "./constants";
import { isMobileEmbeddedView } from "./utils/helpers";
import { getCookieValue } from "./utils/storage";

export interface CookieConsent {
  necessary?: boolean;
  preferences?: boolean;
  statistics?: boolean;
  marketing?: boolean;
}

export interface OneTrustApi {
  IsAlertBoxClosed: boolean;
  OnConsentChanged: (callback: (event: CustomEvent<string[]>) => void) => void;
}

export default class OneTrustConsent {
  private static readonly ONE_TRUST_COOKIE_NAME = "OptanonConsent";
  private static readonly POLLING_INTERVAL_MS = 500;
  private static readonly CONSENT_EVENT_TIMEOUT_MS = import.meta.env.STORYBOOK ? 0 : 30000;
  private static readonly REQUIRED_CONSENT_GROUPS = ["marketing", "statistics"];
  private static activeConsentPromise: Promise<CookieConsent | undefined> | null = null;

  /**
   * Asynchronously gets the consent. If no consent is found,
   * it waits for the user to interact with the OneTrust banner.
   * * @param countryId - The ISO country code (e.g. "DE", "CZ") to check against supported OneTrust countries.
   * @returns A Promise resolving to an object containing the user's active consent groups, or undefined if consent is not applicable, in a mobile embedded view, or the banner wait times out.
   */
  public static async getConsent(countryId: string): Promise<CookieConsent | undefined> {
    if (typeof window === "undefined") {
      return;
    }

    if (isMobileEmbeddedView()) {
      console.warn("OneTrust consent not available in mobile embedded view.");
      return;
    }

    if (!ONE_TRUST_CONSENT_COUNTRIES.includes(countryId.toUpperCase())) {
      console.warn(`OneTrust consent not available for country "${countryId}".`);
      return;
    }

    const storedConsent = this.getConsentSync();
    if (storedConsent) {
      return storedConsent;
    }

    this.activeConsentPromise ??= this.waitForUserConsent().finally(() => {
      this.activeConsentPromise = null;
    });

    return this.activeConsentPromise;
  }

  /**
   * Synchronously reads the current consent state from the cookie.
   * Use this when you need the immediate status without waiting for the banner.
   */
  public static getConsentSync(): CookieConsent | undefined {
    const consentData = OneTrustConsent.getConsentCookie();
    if (!consentData) return undefined;

    const groupsString = new URLSearchParams(consentData).get("groups");
    if (!groupsString) return undefined;

    const groups = groupsString.split(",").reduce((acc, groupPair) => {
      const [key, value] = groupPair.split(":");
      const mappedKey = CONSENT_ONE_TRUST_GROUP_MAPPING[key];

      if (mappedKey && value === "1") {
        acc[mappedKey] = true;
      }
      return acc;
    }, {} as CookieConsent);

    const isValidConsent = OneTrustConsent.REQUIRED_CONSENT_GROUPS.every((key) => key in groups);
    return isValidConsent ? groups : undefined;
  }

  private static waitForUserConsent(): Promise<CookieConsent | undefined> {
    return new Promise((resolve) => {
      const attachListener = () => {
        const oneTrust = globalThis.OneTrust;

        if (!oneTrust) {
          resolve(undefined);
          return;
        }

        oneTrust.OnConsentChanged((event: CustomEvent<string[]>) => {
          const parsed = OneTrustConsent.parseConsentDetail(event.detail || []);
          resolve(parsed);
        });
      };

      const isOneTrustReady = () => {
        return globalThis.OneTrust && typeof globalThis.OneTrust.OnConsentChanged === "function";
      };

      if (isOneTrustReady()) {
        attachListener();
      } else {
        // eslint-disable-next-line prefer-const
        let timeoutId: ReturnType<typeof setTimeout>;

        const checkInterval = setInterval(() => {
          if (isOneTrustReady()) {
            clearInterval(checkInterval);
            clearTimeout(timeoutId);
            attachListener();
          }
        }, OneTrustConsent.POLLING_INTERVAL_MS);

        timeoutId = setTimeout(() => {
          clearInterval(checkInterval);
          console.warn("OneTrust wait timeout expired.");
          resolve(undefined);
        }, OneTrustConsent.CONSENT_EVENT_TIMEOUT_MS);
      }
    });
  }

  private static parseConsentDetail(activeGroupIds: string[]): CookieConsent | undefined {
    const groups = activeGroupIds.reduce((acc, id) => {
      const mappedKey = CONSENT_ONE_TRUST_GROUP_MAPPING[id];
      if (mappedKey) {
        acc[mappedKey] = true;
      }
      return acc;
    }, {} as CookieConsent);

    const isValid = OneTrustConsent.REQUIRED_CONSENT_GROUPS.every((key) => key in groups);
    return isValid ? groups : undefined;
  }

  private static getConsentCookie() {
    try {
      const rawCookieValue = getCookieValue(OneTrustConsent.ONE_TRUST_COOKIE_NAME);
      if (!rawCookieValue) return null;
      return decodeURIComponent(rawCookieValue);
    } catch {
      console.error("Error getting OneTrust consent cookie.");
      return null;
    }
  }
}
